import React, { useEffect } from "react";
import { MapContainer, TileLayer, Circle, useMap, Marker, Popup } from "react-leaflet";
import { Spin, Divider } from "antd";
import QuoteMarker from "./QuoteMarker";
import MapLegend from "./MapLegend";
import { competitorIcon, partnerIcon } from "./markerIcons";

// Component to control map view and add click handler
function MapController({ 
  handleMapClick, 
  isSelectingLocation, 
  customLocation, 
  useCustomLocation, 
  proximityRange,
  mapRef 
}) {
  const map = useMap();
  
  // Register map instance in ref
  useEffect(() => {
    if (mapRef) {
      mapRef.current = map;
    }
  }, [map, mapRef]);
  
  // Add click handler when in selection mode
  useEffect(() => {
    if (isSelectingLocation) {
      map.on('click', handleMapClick);
    } else {
      map.off('click', handleMapClick);
    }
    
    return () => {
      map.off('click', handleMapClick);
    };
  }, [map, isSelectingLocation, handleMapClick]);
  
  // Center map when custom location changes
  useEffect(() => {
    if (customLocation && useCustomLocation) {
      map.setView([customLocation.lat, customLocation.lng], map.getZoom());
    }
  }, [map, customLocation, useCustomLocation]);
  
  return null;
}

export default function QuoteMapContent({
  filteredQuotes,
  quoteCoords,
  customLocation,
  useCustomLocation,
  isSelectingLocation,
  proximityRange,
  handleMapClick,
  mapRef,
  filtering,
  useProximityFilter,
  userLocation,
  showCompetitors,
  competitors,
  showPartners,
  partners
}) {
  // Get center of Italy as default view
  const italyCenter = { lat: 42.504154, lng: 12.646361 };
  const defaultZoom = 6;
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: "calc(100vh - 130px)" }}>
      <div style={{ flex: 1, width: "100%", position: "relative" }}>
        {filtering && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              zIndex: 1000,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Spin size="large" />
          </div>
        )}
        
        <MapContainer
          center={italyCenter}
          zoom={defaultZoom}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Controller component for map actions */}
          <MapController
            handleMapClick={handleMapClick}
            isSelectingLocation={isSelectingLocation}
            customLocation={customLocation}
            useCustomLocation={useCustomLocation}
            proximityRange={proximityRange}
            mapRef={mapRef}
          />
          
          {/* Draw proximity circle for both custom location and user location */}
          {useProximityFilter && (
            useCustomLocation && customLocation ? (
              <Circle
                center={[customLocation.lat, customLocation.lng]}
                radius={proximityRange * 1000} // Convert to meters
                pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.1 }}
              />
            ) : userLocation && (
              <Circle
                center={[userLocation.latitude, userLocation.longitude]}
                radius={proximityRange * 1000} // Convert to meters
                pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.1 }}
              />
            )
          )}
          
          {/* Partner markers */}
          {showPartners && partners && partners.map((partner, index) => (
            <Marker 
              key={`partner-${index}`} 
              position={[partner.lat, partner.lng]}
              icon={partnerIcon}
            >
              <Popup>
                <div>
                  <strong style={{ background: 'linear-gradient(to right, red, orange, yellow, green, blue, indigo, violet)', '-webkit-background-clip': 'text', '-webkit-text-fill-color': 'transparent' }}>
                    <span role="img" aria-label="partner">🤝</span> PARTNER
                  </strong><br />
                  <Divider style={{ margin: '8px 0' }} />
                  <strong>
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partner.name)}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {partner.name}
                    </a>
                  </strong><br />
                  <Divider style={{ margin: '8px 0' }} />
                  <strong>Indirizzo:</strong><br />
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partner.address)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {partner.address}
                  </a>
                  <Divider style={{ margin: '8px 0' }} />
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${partner.lat},${partner.lng}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Apri coordinate in Google Maps
                  </a>
                </div>
              </Popup>
            </Marker>
          ))}
          
          {/* Competitor markers */}
          {showCompetitors && competitors && competitors.map((competitor, index) => (
            <Marker 
              key={`competitor-${index}`} 
              position={[competitor.lat, competitor.lng]}
              icon={competitorIcon}
            >
              <Popup>
                <div>
                  <strong style={{ color: '#000000' }}>
                    <span role="img" aria-label="competitor">⚠️</span> COMPETITOR
                  </strong><br />
                  <Divider style={{ margin: '8px 0' }} />
                  <strong>
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(competitor.name)}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {competitor.name}
                    </a>
                  </strong><br />
                  <Divider style={{ margin: '8px 0' }} />
                  <strong>Indirizzo:</strong><br />
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(competitor.address)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {competitor.address}
                  </a>
                  <Divider style={{ margin: '8px 0' }} />
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${competitor.lat},${competitor.lng}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Apri coordinate in Google Maps
                  </a>
                </div>
              </Popup>
            </Marker>
          ))}
          
          {/* Render quote markers */}
          {filteredQuotes.map((quote) => {
            // Find coordinate for this quote's customer
            const quoteCoord = quoteCoords.find(
              coord => coord.cliente_pointer?.objectId === quote.customer_pointer?.objectId
            );
            
            // Only display if we have coordinates
            if (quoteCoord) {
              return (
                <QuoteMarker 
                  key={quote.objectId} 
                  quote={quote} 
                  coord={quoteCoord} 
                />
              );
            }
            
            return null;
          })}
        </MapContainer>
      </div>
      
      {/* Legenda orizzontale sotto la mappa */}
      <MapLegend 
        useProximityFilter={useProximityFilter} 
        proximityRange={proximityRange}
        showCompetitors={showCompetitors}
        showPartners={showPartners}
      />
    </div>
  );
}